import { Point } from 'pixi.js';

import { Shared } from '../../App';
import { SplineDisplay } from '../../display/SplineDisplay';
import { Segment } from '../../utils/Segment';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { BaseEntity } from './BaseEntity';

export interface SegmentEntityParams
{
    type: string,
    precision: number,
    thickness: number,
    baseline: number,
    x: number,
    y: number,
    img: string,
    maxPrecision: number,
    colinearAngle: number,
    verts: {x: number, y: number, cx: number, cy: number}[]
    props?: {z?: number}
}

export class SegmentEntity extends BaseEntity
{
    public spline?: SplineDisplay;
    public culling: CullingComponent;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
    }

    public build(params: SegmentEntityParams): void
    {
        const maxPrecision = params.maxPrecision || 16;
        const colinearAngle = params.colinearAngle || 0.01;
        const list = Segment.getPointsFromVerts(params.verts, false, maxPrecision, colinearAngle);
        const points = list.map((p) => new Point(p.x, p.y));

        this.updateSpline(params, points);

        this.x = params.x + ((this.spline?.width || 0) / 2);
        this.y = params.y + ((this.spline?.height || 0) / 2);
        this.z = params.props?.z ?? 0;

        this.culling.updateBounds();
    }

    private updateSpline(params: SegmentEntityParams, points: Point[]): void
    {
        const splinePoints = points.slice(0);

        if (this.spline)
        {
            if (this.spline.parent) this.spline.parent.removeChild(this.spline);
            this.spline.destroy();
            this.spline = undefined;
        }

        if (!params.img) return;

        const splineTexture = Shared.assets.getTexture(params.img);

        this.spline = new SplineDisplay(splinePoints, splineTexture, {
            thickness: params.thickness || splineTexture.height,
            baseline: params.baseline,
            textureScale: 1,
            repeatDistance: 512,
        });
        this.spline.x = -this.spline.width / 2;
        this.spline.y = -this.spline.height / 2;
        this.view.addChild(this.spline);
    }
}

export class SegmentFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SegmentEntity';
    }

    public static create(game: Game, params: Record<string, any>): void
    {
        const ground = game.pool.get(SegmentEntity) as SegmentEntity;

        ground.build(params as SegmentEntityParams);
        game.level.addEntity(ground);
    }
}
